'use client';
// ════════════════════════════════════════════════════════════════════════════
// PWA INSTALL BANNER — assistant d'installation universel (tous appareils)
// ────────────────────────────────────────────────────────────────────────────
// Affiche le BON chemin d'installation selon l'appareil :
//   • Android / Chrome / Edge : vrai bouton « Installer » (beforeinstallprompt).
//   • iPhone / iPad Safari : étapes guidées « Partager → Sur l'écran d'accueil »
//     (Apple n'autorise AUCUN bouton auto — limite système, pas un choix).
//   • Chrome/Firefox sur iOS : invite à ouvrir dans Safari (eux non plus ne
//     peuvent pas installer).
// Ne s'affiche PAS si l'app est déjà installée (mode standalone), et se souvient
// du rejet (14 j). i18n FR/NL/EN/DE. Aucun cache, aucune dépendance.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import AureusLogo from './AureusLogo';

const G = '#c6a34e';
const BG = '#12141b';
const TEXT = '#e8e6e0';
const MUTED = '#9e9b93';
const BORDER = 'rgba(198,163,78,.30)';
const DISMISS_KEY = 'aureus_pwa_dismissed_at';
const DISMISS_DAYS = 14;

const T = {
  fr: {
    title: 'Installer Aureus', sub: 'Accès en un clic, comme une appli — gratuit, sans store.',
    install: 'Installer l\'application', later: 'Plus tard',
    iosA: 'Appuie sur', iosShare: 'Partager', iosB: 'en bas de Safari,', iosC: 'puis « Sur l\'écran d\'accueil ».',
    iosChrome: 'Pour installer, ouvre cette page dans', iosChromeB: 'Safari', iosChromeC: '(règle d\'Apple).',
  },
  nl: {
    title: 'Aureus installeren', sub: 'Eén klik, als een app — gratis, zonder store.',
    install: 'App installeren', later: 'Later',
    iosA: 'Tik op', iosShare: 'Delen', iosB: 'onderaan Safari,', iosC: 'dan « Zet op beginscherm ».',
    iosChrome: 'Om te installeren, open deze pagina in', iosChromeB: 'Safari', iosChromeC: '(regel van Apple).',
  },
  en: {
    title: 'Install Aureus', sub: 'One-tap access, like an app — free, no store.',
    install: 'Install the app', later: 'Later',
    iosA: 'Tap', iosShare: 'Share', iosB: 'at the bottom of Safari,', iosC: 'then “Add to Home Screen”.',
    iosChrome: 'To install, open this page in', iosChromeB: 'Safari', iosChromeC: '(Apple\'s rule).',
  },
  de: {
    title: 'Aureus installieren', sub: 'Ein-Klick-Zugang wie eine App — gratis, ohne Store.',
    install: 'App installieren', later: 'Später',
    iosA: 'Tippe auf', iosShare: 'Teilen', iosB: 'unten in Safari,', iosC: 'dann „Zum Home-Bildschirm“.',
    iosChrome: 'Zum Installieren diese Seite in', iosChromeB: 'Safari', iosChromeC: 'öffnen (Apple-Regel).',
  },
};

function pickLang() {
  try {
    const saved = localStorage.getItem('aureus_lang');
    if (saved && T[saved]) return saved;
  } catch { /* noop */ }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'fr').slice(0, 2).toLowerCase();
  return T[nav] ? nav : 'fr';
}

const isStandalone = () =>
  (typeof window !== 'undefined') &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

function detectPlatform() {
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua) ||
    (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIOSSafari = isIOS && /safari/i.test(ua) && !/crios|fxios|edgios|chrome/i.test(ua);
  return { isIOS, isIOSSafari };
}

// Le vrai écusson de marque (28/07/2026) : la bannière affichait jusqu'ici les
// initiales « AS » sur un carré doré — un ersatz, pas le logo. L'utilisateur voit
// désormais exactement l'icône qu'il va épingler sur son écran d'accueil.

// Icône « Partager » iOS (carré + flèche vers le haut).
function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', margin: '0 2px' }} aria-hidden="true">
      <path d="M12 3v13" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  );
}

export default function PWAInstallBanner() {
  const [lang, setLang] = useState('fr');
  const [deferred, setDeferred] = useState(null); // event beforeinstallprompt (Android/desktop)
  const [plat, setPlat] = useState({ isIOS: false, isIOSSafari: false });
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // déjà installé → rien
    // Rejet récent ?
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (at && (Date.now() - at) < DISMISS_DAYS * 86400000) return;
    } catch { /* noop */ }

    setLang(pickLang());
    const p = detectPlatform();
    setPlat(p);

    const onBip = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', onBip);

    // Sur iOS Safari, beforeinstallprompt n'existe pas → on montre les étapes après un délai.
    // Sur Android/Chrome/desktop, c'est onBip qui déclenche setShow(true) — le timer ne doit
    // pas s'activer hors iOS, sinon show=true avant que deferred soit prêt et le re-render
    // de setDeferred(e) ne se produit plus (show était déjà true → React ne re-rend pas).
    const timer = p.isIOS ? setTimeout(() => setShow(true), 2500) : null;

    return () => { window.removeEventListener('beforeinstallprompt', onBip); clearTimeout(timer); };
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setShow(false);
  }, []);

  const doInstall = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* noop */ }
    setDeferred(null);
    dismiss();
  }, [deferred, dismiss]);

  // Décider quoi afficher.
  const showInstallButton = !!deferred;           // Android / Chrome / Edge
  const showIOSSteps = plat.isIOSSafari;           // iPhone/iPad Safari
  const showIOSChrome = plat.isIOS && !plat.isIOSSafari; // Chrome/Firefox sur iOS
  const renderable = showInstallButton || showIOSSteps || showIOSChrome;
  if (!show || !renderable) return null;

  const t = T[lang] || T.fr;

  return (
    <div role="dialog" aria-label={t.title} style={{
      position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
      maxWidth: 440, margin: '0 auto', background: BG, color: TEXT,
      border: `1px solid ${BORDER}`, borderRadius: 14, padding: '14px 16px',
      boxShadow: '0 8px 30px rgba(0,0,0,.45)', fontSize: 14, lineHeight: 1.5,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <AureusLogo size={40} priority />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: G, marginBottom: 2, fontSize: 13, letterSpacing: '1.5px', textTransform: 'uppercase', lineHeight: 1.2 }}>Aureus Social Pro</div>
          <div style={{ color: MUTED, fontSize: 9.5, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 2, lineHeight: 1.3 }}>Payroll management Belgium</div>

          {showInstallButton && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button onClick={doInstall} style={{
                background: G, color: '#0a0a0f', border: 'none', borderRadius: 9,
                padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13.5,
              }}>{t.install}</button>
              <button onClick={dismiss} style={{
                background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`,
                borderRadius: 9, padding: '9px 14px', cursor: 'pointer', fontSize: 13,
              }}>{t.later}</button>
            </div>
          )}

          {showIOSSteps && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              {t.iosA} <ShareIcon /><b style={{ color: G }}>{t.iosShare}</b> {t.iosB} {t.iosC}
            </div>
          )}

          {showIOSChrome && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              {t.iosChrome} <b style={{ color: G }}>{t.iosChromeB}</b> {t.iosChromeC}
            </div>
          )}
        </div>

        <button onClick={dismiss} aria-label={t.later} style={{
          background: 'transparent', color: MUTED, border: 'none', cursor: 'pointer',
          fontSize: 18, lineHeight: 1, padding: 2,
        }}>×</button>
      </div>
    </div>
  );
}
